import type { ComponentChildren } from 'preact';
import { RouteProvider, type UseRoute } from '@/widgets/hooks/useRoute.tsx';
import { MarketProvider } from '@/widgets/hooks/useMarketConfig.tsx';
import { WidgetOptions } from '@/widgets/hooks/useWidgetOptions.tsx';
import { TranslationsProvider } from '@/widgets/hooks/useTranslations.tsx';
import { QueryStringStoreProvider } from '@/widgets/hooks/useQueryStringStore.tsx';
import { defaultOptions, defaultTranslations } from './config/index.ts';
import type { WidgetConfig } from '@/domain/WidgetConfig.ts';
import type { Market } from '@/domain/Market.ts';
import {
  StaticContentProvider,
  type StaticContent
} from '@/widgets/hooks/useStaticContent.tsx';
import updateQueryState from '../utils/updateQueryState.ts';
import { MediaQueryProvider } from '../hooks/useMediaQuery.tsx';
import { getI18nPriceFormatter } from '../hooks/usePriceFormatter.tsx';
import { ABTestProvider } from '../hooks/useABTestVariant.tsx';
import { FavoritesProvider } from '../hooks/useFavorites.tsx';

export type ProductDetailsPageProps = {
  route: UseRoute;
  config: WidgetConfig;
  marketConfig: Market;
  staticContent?: StaticContent;
};

type ProductDetailsProviderProps = ProductDetailsPageProps & {
  children: ComponentChildren;
};

export const namespace = 'pdh';

export default function Providers({
  children,
  route,
  config,
  marketConfig,
  staticContent
}: ProductDetailsProviderProps) {
  const customFormatters = getI18nPriceFormatter(
    marketConfig.financialSettings.defaultCurrency,
    marketConfig.priceFormat
  );

  return (
    <RouteProvider {...route}>
      <MarketProvider {...marketConfig}>
        <ABTestProvider>
          <WidgetOptions config={config.options} defaults={defaultOptions}>
            <StaticContentProvider {...staticContent}>
              <TranslationsProvider
                locale={route.locale}
                config={config.translations}
                defaults={defaultTranslations}
                customFormatters={customFormatters}
              >
                <QueryStringStoreProvider
                  namespace={namespace}
                  location={globalThis?.location}
                  navigate={updateQueryState}
                >
                  <FavoritesProvider>
                    <MediaQueryProvider>{children}</MediaQueryProvider>
                  </FavoritesProvider>
                </QueryStringStoreProvider>
              </TranslationsProvider>
            </StaticContentProvider>
          </WidgetOptions>
        </ABTestProvider>
      </MarketProvider>
    </RouteProvider>
  );
}
